import { sortBy } from "lodash";
import { createSelector } from "reselect";

import { SaleType } from "constants/sale";

import { EMPTY_ARRAY } from "lib";

import { attributeOrderSort } from "lib/speciesAttributes";

import {
  getCurrentSale,
  getCurrentOrFirstSale,
  selectCurrentDeploymentIds,
} from "./currentSale";
import { selectActiveProducts } from "./products";
import { getDeployments, getSpeciesAttributes } from "./root";
import { getDeploymentAttributes } from "./root.state";

export interface AttributeOption {
  label: string;
  value: number;
  quick_code: string | null;
}

// DeploymentAge has no `quick_code` field on the backend (confirmed against
// the Django serializers), so reading it has always resolved to `undefined`,
// falling through to the name fallback below. DeploymentSex has neither
// `quick_code` nor `quick_select`, so its quick-select feature has always
// evaluated to null. Left as-is in both cases; these only widen the type
// enough to compile without changing that behaviour.
type WithNoOpQuickCode<T> = T & { quick_code?: string };
type WithNoOpQuickSelect<T> = T & { quick_select?: boolean };

export const getActiveDeploymentAttributes = createSelector(
  [getDeploymentAttributes],
  (deploymentAttributes: DeploymentAttributesStateSlice) =>
    Object.entries(deploymentAttributes).reduce(
      (acc, [attributeName, attributeValues]) => {
        // Object.values() loses the fact that every collection here is
        // really a DeploymentAttribute[], so it's reasserted below.
        acc[attributeName] = (
          Object.values(attributeValues) as DeploymentAttribute[]
        )
          .filter(a => !a.deprecated && !a.deleted)
          .sort(attributeOrderSort);
        return acc;
      },
      {} as Record<string, DeploymentAttribute[]>,
    ),
);

export const selectDeploymentBreedsByDeploymentIdLookup = createSelector(
  [
    getCurrentOrFirstSale,
    getDeploymentAttributes,
    getSpeciesAttributes,
    selectCurrentDeploymentIds,
  ],
  (
    sale: Sale,
    deploymentAttributes: DeploymentAttributesStateSlice,
    speciesAttributes: { breeds: Record<string, Breed> },
    currentDeploymentIds: number[],
  ): Record<string, AttributeOption[]> => {
    const defaults = currentDeploymentIds.reduce(
      (acc, cur) => {
        acc[cur] = [];
        return acc;
      },
      {} as Record<string, AttributeOption[]>,
    );

    const getBreedQuickSelectKey = (saleType: SaleType) => {
      switch (saleType) {
        case SaleType.PADDOCK:
          return "quick_select_paddock";
        case SaleType.OVER_HOOKS:
          return "quick_select_hooks";
        default:
          return "quick_select";
      }
    };

    const breedQuickSelectKey = getBreedQuickSelectKey(sale.sale_type);

    return sortBy(Object.values(deploymentAttributes.breeds), "order").reduce(
      (acc, db) => {
        const masterBreed = speciesAttributes.breeds[db.breed_id] || {
          name: "Loading Breeds",
        };
        if (
          "species_id" in masterBreed &&
          masterBreed.species_id === sale.species_id &&
          db.is_used
        ) {
          const quickCode = db[breedQuickSelectKey]
            ? db.quick_code
              ? db.quick_code
              : masterBreed.name
            : null;

          const entry: AttributeOption = {
            label: masterBreed.name,
            value: db.breed_id,
            quick_code: quickCode,
          };

          if (Array.isArray(acc[db.deployment_id])) {
            acc[db.deployment_id].push(entry);
          } else {
            acc[db.deployment_id] = [entry];
          }
        }
        return acc;
      },
      defaults,
    );
  },
);

export const selectDeploymentAgesByDeploymentIdLookup = createSelector(
  [getCurrentSale, getDeploymentAttributes, getSpeciesAttributes],
  (
    sale: Sale,
    deploymentAttributes: DeploymentAttributesStateSlice,
    speciesAttributes: { ages: Record<string, Age> },
  ): Record<string, AttributeOption[]> =>
    (
      sortBy(
        Object.values(deploymentAttributes.ages),
        "order",
      ) as WithNoOpQuickCode<DeploymentAge>[]
    ).reduce(
      (acc, db) => {
        const age = speciesAttributes.ages[db.age_id];
        if (age && age.species_id === sale.species_id && db.is_used) {
          const quickCode = db.quick_select
            ? db.quick_code
              ? db.quick_code
              : age.name
            : null;

          const entry: AttributeOption = {
            label: age.name,
            value: db.age_id,
            quick_code: quickCode,
          };

          if (typeof acc[db.deployment_id] === "undefined") {
            acc[db.deployment_id] = [entry];
          } else {
            acc[db.deployment_id].push(entry);
          }
        }
        return acc;
      },
      {} as Record<string, AttributeOption[]>,
    ),
);

export const selectDeploymentSexesByDeploymentIdLookup = createSelector(
  [getCurrentSale, getDeploymentAttributes, getSpeciesAttributes],
  (
    sale: Sale,
    deploymentAttributes: DeploymentAttributesStateSlice,
    speciesAttributes: { sexes: Record<string, Sex> },
  ): Record<string, AttributeOption[]> =>
    (
      sortBy(
        Object.values(deploymentAttributes.sexes),
        "order",
      ) as (WithNoOpQuickCode<DeploymentSex> &
        WithNoOpQuickSelect<DeploymentSex>)[]
    ).reduce(
      (lookup, sex) => {
        const speciesSex = speciesAttributes.sexes[sex.sex_id];
        if (
          speciesSex &&
          speciesSex.species_id === sale.species_id &&
          sex.is_used
        ) {
          const quickCode = sex.quick_select
            ? sex.quick_code || speciesSex.name
            : null;

          const entry: AttributeOption = {
            label: speciesSex.name,
            value: sex.sex_id,
            quick_code: quickCode,
          };

          if (typeof lookup[sex.deployment_id] === "undefined") {
            lookup[sex.deployment_id] = [entry];
          } else {
            lookup[sex.deployment_id].push(entry);
          }
        }
        return lookup;
      },
      {} as Record<string, AttributeOption[]>,
    ),
);

export const getDeploymentBreedOptionsByDeploymentId =
  (deploymentId: number) => (state: RootState) =>
    selectDeploymentBreedsByDeploymentIdLookup(state)[deploymentId] ||
    EMPTY_ARRAY;

export const getDeploymentAgeOptionsByDeploymentId =
  (deploymentId: number) => (state: RootState) =>
    selectDeploymentAgesByDeploymentIdLookup(state)[deploymentId] ||
    EMPTY_ARRAY;

// { [speciesId]: { [deploymentId]: AgeOption[] } }
// Use when no current sale exists (e.g. create-sale forms) and species is known from context.
export const selectDeploymentAgesBySpeciesIdAndDeploymentIdLookup =
  createSelector(
    [getDeploymentAttributes, getSpeciesAttributes],
    (
      deploymentAttributes: DeploymentAttributesStateSlice,
      speciesAttributes: { ages: Record<string, Age> },
    ): Record<string, Record<string, AttributeOption[]>> =>
      (
        sortBy(
          Object.values(deploymentAttributes.ages),
          "order",
        ) as WithNoOpQuickCode<DeploymentAge>[]
      ).reduce(
        (acc, db) => {
          const age = speciesAttributes.ages[db.age_id];
          if (!age || !db.is_used) return acc;
          const quickCode = db.quick_select ? db.quick_code || age.name : null;
          const entry: AttributeOption = {
            label: age.name,
            value: db.age_id,
            quick_code: quickCode,
          };
          if (!acc[age.species_id]) acc[age.species_id] = {};
          if (acc[age.species_id][db.deployment_id]) {
            acc[age.species_id][db.deployment_id].push(entry);
          } else {
            acc[age.species_id][db.deployment_id] = [entry];
          }
          return acc;
        },
        {} as Record<string, Record<string, AttributeOption[]>>,
      ),
  );

export const getDeploymentAgeOptionsByDeploymentIdAndSpeciesId =
  (deploymentId: number, speciesId: number | null) => (state: RootState) =>
    selectDeploymentAgesBySpeciesIdAndDeploymentIdLookup(state)[speciesId]?.[
      deploymentId
    ] ?? EMPTY_ARRAY;

// { [speciesId]: { [deploymentId]: BreedOption[] } }
// Use when no current sale exists (e.g. create-sale forms) and species is known from context.
export const selectDeploymentBreedsBySpeciesIdAndDeploymentIdLookup =
  createSelector(
    [getDeploymentAttributes, getSpeciesAttributes],
    (
      deploymentAttributes: DeploymentAttributesStateSlice,
      speciesAttributes: { breeds: Record<string, Breed> },
    ): Record<string, Record<string, AttributeOption[]>> =>
      sortBy(Object.values(deploymentAttributes.breeds), "order").reduce(
        (acc, db) => {
          const masterBreed = speciesAttributes.breeds[db.breed_id];
          if (!masterBreed || !db.is_used) return acc;
          const quickCode = db.quick_select
            ? db.quick_code || masterBreed.name
            : null;
          const entry: AttributeOption = {
            label: masterBreed.name,
            value: db.breed_id,
            quick_code: quickCode,
          };
          if (!acc[masterBreed.species_id]) acc[masterBreed.species_id] = {};
          if (Array.isArray(acc[masterBreed.species_id][db.deployment_id])) {
            acc[masterBreed.species_id][db.deployment_id].push(entry);
          } else {
            acc[masterBreed.species_id][db.deployment_id] = [entry];
          }
          return acc;
        },
        {} as Record<string, Record<string, AttributeOption[]>>,
      ),
  );

export const getDeploymentBreedOptionsByDeploymentIdAndSpeciesId =
  (deploymentId: number, speciesId: number | null) => (state: RootState) =>
    selectDeploymentBreedsBySpeciesIdAndDeploymentIdLookup(state)[speciesId]?.[
      deploymentId
    ] ?? EMPTY_ARRAY;

export const selectAllAttributes = createSelector(
  [getDeploymentAttributes, selectActiveProducts],
  (
    deploymentAttributes: DeploymentAttributesStateSlice,
    products: Record<string, Product>,
  ) => ({
    ...deploymentAttributes,
    products,
  }),
);

type AllAttributes = ReturnType<typeof selectAllAttributes>;

// Each attribute collection (ages/breeds/sexes/labels/marks/dentition/products)
// has a different value shape, and the loop below has to build the result
// generically - Object.keys()/values() can't carry per-key precision through
// a loop like that. This mapped type derives the real per-key shape (ages:
// DeploymentAge[], products: Product[], etc.) from `AllAttributes` itself, so
// the one cast at the end of the loop is all that's needed to get it back,
// rather than the result staying `unknown[]` for every collection.
type AttributesByDeploymentId = {
  [K in keyof AllAttributes]: Array<
    AllAttributes[K] extends Record<string, infer V> ? V : never
  >;
};

export const selectAllAttributesByDeploymentIdLookup = createSelector(
  [getDeployments, selectAllAttributes],
  (
    deployments: Record<string, Deployment>,
    allAttributes: AllAttributes,
  ): Record<string, AttributesByDeploymentId> =>
    Object.keys(deployments).reduce(
      (acc, deploymentId) => {
        acc[deploymentId] = Object.keys(allAttributes).reduce(
          (attrAcc, attribute) => {
            attrAcc[attribute] = (
              Object.values(allAttributes[attribute]) as {
                deployment_id: number;
              }[]
            ).filter(a => `${a.deployment_id}` === deploymentId);
            return attrAcc;
          },
          {} as Record<string, unknown[]>,
        ) as AttributesByDeploymentId;
        return acc;
      },
      {} as Record<string, AttributesByDeploymentId>,
    ),
);
